import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataStorage } from '../core/data/data-storage';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubscriptionService } from '../core/subscription/subscription.service';
import { AnalyticsEvent, InternalAnalyticsService } from '../core/internal-analytics.service';
import { GuidedSetupService, GuidedSetupStepType } from '../guided-setup/guided-setup.service';
import { UserService } from '../core/user/user.service';
import { AgentService } from '../core/agent/agent.service';
import { SessionService } from '../core/session.service';
import { isDirectSignupVariant } from '../core/helpers/signup-variant';
import { HubspotContactLimitToastService } from '../core/business/hubspot-contact-limit-toast.service';
import { DisconnectedIntegrationToastService } from '../core/business/disconnected-integration-toast.service';
import { CallRateLimitService } from '../core/call-rate-limit/call-rate-limit.service';
import { WinbackGateService } from './winback-modal/winback-gate.service';

@Component({
  selector: 'rosie-dashboard',
  template: `
    <div class="paused-banner" *ngIf="isSubscriptionPaused">
      <div class="paused-banner-content">
        <div class="paused-badge">
          <ng-icon [name]="'heroPauseCircleMini'"></ng-icon>
          <span class="paused-badge-text">{{'COMMON.PAUSED' | translate}}</span>
        </div>
        <div class="paused-text">
          <span class="paused-text-bold">{{'COMMON.PAUSED_BANNER_TEXT' | translate}}</span>
          <span class="paused-text-link" (click)="unpauseFromBanner()">{{'COMMON.UNPAUSE_LINK' | translate}}</span>
        </div>
      </div>
    </div>
     <div class="payment-failed-banner" *ngIf="showPaymentFailedBanner">
      <ng-icon [name]="'heroExclamationTriangleSolid'" [color]="'#FDA4AF'" class="payment-failed-icon"
            [size]="'20px'"></ng-icon>
      <span class="d-block ml-1 mr-4">{{bannerAlertTranslationKey | translate}}
        <span class="payment-action" *ngIf="isMobileVersion && !(isPendingApproval && isAppleBillingProvider)" (click)="handlePaymentFailed()">
          {{bannerActionTranslationKey | translate}}
          <ng-icon class="payment-arrow-icon" [name]="'heroArrowRight'" [size]="'14px'" [color]="'#ffffff'"></ng-icon>
        </span>
      </span>
       <rosie-button *ngIf="!isMobileVersion && !(isPendingApproval && isAppleBillingProvider)" (click)="handlePaymentFailed()" class="d-block" [customClass]="'narrow slim dark'" [inProgress]="isRefreshingState"
         [iconSuffixConfig]="{name: 'heroArrowRight', size: '18px', strokeWidth: '2', color: '#C1BBCA'}"
        [translationKey]="bannerActionTranslationKey">
        </rosie-button>
    </div>
    <div class="test-mode-banner" *ngIf="showStatusBanner">
      <div class="status-badge" [ngClass]="isLiveMode ? 'live' : 'test'">{{isLiveMode ? 'LIVE' : 'TEST MODE'}}</div>
      <div class="status-text">{{isLiveMode ? 'Your agent can receive calls from anyone.' : 'Your agent is in test mode and cannot receive external calls.'}}
        <span class="complete-setup-action" *ngIf="!isLiveMode && isMobileVersion" (click)="completeSetup()">{{'Finish Setup' | translate}}
          <ng-icon class="setup-arrow-icon" [name]="'heroArrowRight'" [size]="'14px'" [color]="'#fbbf24'"></ng-icon>
        </span>
      </div>
      <rosie-button *ngIf="!isLiveMode && !isMobileVersion" [translationKey]="'Finish Setup'" [iconSuffixConfig]="{name: 'heroArrowRight', size: '18px', strokeWidth: '2', color: '#92400E'}"
      [customClass]="'slimmer text-sm gold-alert narrow'" (click)="completeSetup()"></rosie-button>
      <div class="cancel-button" *ngIf="isLiveMode" (click)="dismissLiveBanner()">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 21.732 21.732 28 14 28C6.26801 28 0 21.732 0 14Z" fill="white" fill-opacity="0.08"/>
          <path d="M11.0243 10.1757C10.7899 9.94142 10.4101 9.94142 10.1757 10.1757C9.94142 10.4101 9.94142 10.7899 10.1757 11.0243L13.1515 14L10.1757 16.9757C9.94142 17.2101 9.94142 17.59 10.1757 17.8243C10.4101 18.0586 10.7899 18.0586 11.0243 17.8243L14 14.8485L16.9757 17.8243C17.2101 18.0586 17.59 18.0586 17.8243 17.8243C18.0586 17.59 18.0586 17.2101 17.8243 16.9757L14.8485 14L17.8243 11.0243C18.0586 10.7899 18.0586 10.4101 17.8243 10.1757C17.59 9.94142 17.2101 9.94142 16.9757 10.1757L14 13.1515L11.0243 10.1757Z" fill="white"/>
        </svg>
      </div>
    </div>
    <!-- Stacked below the other banners: it never replaces a paused, payment or mode banner (ENG-2263) -->
    <rosie-call-rate-limit-banner *ngIf="showCallRateLimitBanner" [variant]="'top'"></rosie-call-rate-limit-banner>
    <div class="guided-setup-trial-banner" *ngIf="showGuidedSetupTrialBanner" [ngClass]="{'without-action': hasCreditCardOnFile}">
        <div class="guided-setup-trial-banner-title">
          <div class="day-counter">{{trialDaysLeft}}</div>
          Days left in your free trial.
      </div>
        <div class="guided-setup-trial-banner-description" *ngIf="isCardStatusResolved && !hasCreditCardOnFile" (click)="completeSetup()">Go live to accept customer calls.</div>
    </div>
     <div class="d-flex rosie-dashboard-wrapper"
     [ngClass]="{'with-payment-failed-banner':  showPaymentFailedBanner, 'with-mode-banner': showStatusBanner, 'with-guided-setup-trial-banner': showGuidedSetupTrialBanner, 'has-credit-card-on-file': hasCreditCardOnFile, 'with-paused-banner': isSubscriptionPaused, 'with-call-rate-limit-banner': showCallRateLimitBanner}">
      <div [ngClass]="{'hide-sidebar': !showNavSidebar}">
        <rosie-nav-sidebar></rosie-nav-sidebar>
      </div>
      <div class="content-section" [ngClass]="{'has-scrollable-content':  hasScrollableContent, 'has-nav-sidebar-visible': showNavSidebar}">
        <router-outlet></router-outlet>
      </div>
    <div>
    <rosie-winback-modal *ngIf="showWinbackModal"></rosie-winback-modal>
  `,
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  persistentStorage: DataStorage = DataStorage.persistent();
  isRefreshingState = false;

  private onboardingRedirectSub?: Subscription;

  constructor(private router: Router, private subscription: SubscriptionService, private analytics: InternalAnalyticsService, private setup: GuidedSetupService, private user: UserService, private agent: AgentService, private session: SessionService, private hubspotContactLimitToast: HubspotContactLimitToastService, private disconnectedIntegrationToast: DisconnectedIntegrationToastService, private callRateLimits: CallRateLimitService, private winbackGate: WinbackGateService) {
  }

  ngOnInit(): void {
    // The agent is resolved before this shell mounts, so check once on load and
    // again on every navigation (covers account switches, which re-navigate).
    this.redirectToOnboardingIfNeeded();
    this.hubspotContactLimitToast.showIfNeeded();
    this.disconnectedIntegrationToast.showIfNeeded();
    this.callRateLimits.syncFromAgent(this.agent.primary);
    this.callRateLimits.startPolling();
    this.onboardingRedirectSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.redirectToOnboardingIfNeeded();
        this.hubspotContactLimitToast.showIfNeeded();
        this.disconnectedIntegrationToast.showIfNeeded();
      });
  }

  ngOnDestroy(): void {
    this.onboardingRedirectSub?.unsubscribe();
    this.callRateLimits.stopPolling();
  }

  // Canceled business with a trial reset from Nova: block on a card (ENG-2367).
  // Stays up while it finishes, after the subscription has cleared the flag.
  get showWinbackModal(): boolean {
    if (this.winbackGate.completing) {
      return true;
    }
    return !!this.user.activeBusiness?.trialResetPending && !this.subscription.isExternalBillingProvider;
  }

  // The test and launch steps render the same banner inline next to the test
  // numbers, so the shell bar stays out of the way there.
  get showCallRateLimitBanner(): boolean {
    if (!this.callRateLimits.hasActiveLimits) {
      return false;
    }
    if (this.setup.isOnPreLaunchSetupStep) {
      return false;
    }
    if (this.router.url.includes('/setup')) {
      return this.setup.activeStep !== GuidedSetupStepType.Test && this.setup.activeStep !== GuidedSetupStepType.Launch;
    }
    return true;
  }

  /**
   * A Shopify (commerce) account — or a Variant D signup — that hasn't finished
   * onboarding should always land in the setup wizard. Both onboard into an
   * empty agent, so anywhere else in the app is a dead end (and for D the root
   * redirect actively misroutes: it sends card-on-file users to /conversations,
   * and every D user has a card from minute one).
   *
   * Deliberately narrow so no existing account is ever caught:
   *  - commerce accounts and mid-setup Variant D signups only;
   *  - fail open if the agent isn't loaded yet (never redirect on missing data);
   *  - isGuidedSetupCompleted already treats legacy (pre-2025-06-17) and
   *    completed accounts as done, so they're exempt by construction;
   *  - never redirect away from setup / auth / utility routes (no loops).
   */
  private redirectToOnboardingIfNeeded(): void {
    if (!this.agent.primary) return;
    if (!this.needsSetupRedirect()) return;

    const path = this.router.url.split('?')[0].split(';')[0];

    if (path === '' || path === '/') return;

    const exemptPrefixes = [
      '/setup', '/login', '/register', '/oops', '/download',
      '/forgot-password', '/password-reset', '/invitations', '/404',
    ];

    if (exemptPrefixes.some((prefix) => path.startsWith(prefix))) return;

    this.router.navigate(['/setup']);
  }

  // Herd back only while the setup chrome is still hidden (herding past that
  // point made the sidebar useless): the nav returns at the Test step, so
  // commerce herds until Test is reached and the direct-signup variants (C/D)
  // until Customize has been reached.
  private needsSetupRedirect(): boolean {
    if (this.setup.isGuidedSetupCompleted) return false;

    if (this.user.hasCommerceApp) {
      return !this.setup.isTestStepCompleted;
    }

    if (isDirectSignupVariant(this.user.userData?.signupVariant)) {
      return !this.setup.isCustomizeStepCompleted;
    }

    return false;
  }

  get hasScrollableContent(): boolean {
    return this.router.url.includes('/setup');
  }

  get isSubscriptionPastDue(): boolean {
    return this.subscription?.isPastDue && this.subscription?.hasCreditCardOnFile;
  }

  get isSubscriptionCanceled(): boolean {
    return this.subscription?.isCanceled;
  }

  get isPendingApproval(): boolean {
    return this.subscription?.isPendingApproval;
  }

  get isSubscriptionPaused(): boolean {
    return this.subscription?.isPaused;
  }

  // The win-back gate covers the canceled state itself, so no "Rosie is
  // offline" banner behind it.
  get showPaymentFailedBanner(): boolean {
    if (this.showWinbackModal) {
      return false;
    }
    return this.isSubscriptionPastDue || this.isSubscriptionCanceled || this.isPendingApproval;
  }

  get isPaymentFailedTriggered() {
    return this.subscription?.hasPaymentFailedTriggered;
  }

  get hasCreditCardOnFile(): boolean {
    return this.subscription?.hasCreditCardOnFile;
  }

  // On a fresh signup the subscription loads async — until it does, hasCreditCard
  // OnFile reads false and the "Go live to accept external calls" copy flashes,
  // then vanishes once the (card-bearing) subscription arrives. Gate that copy on
  // the subscription actually being resolved so it never flashes.
  get isCardStatusResolved(): boolean {
    return !!this.user.userData?.subscription;
  }

  get paymentFailedTranslationKey() {
    return this.subscription?.hasPaymentFailedTriggered ? 'COMMON.REFRESH_STATUS' : 'COMMON.FIX_IT_NOW';
  }

  get isExternalBillingProvider(): boolean {
    return this.subscription.isExternalBillingProvider;
  }

  get isAppleBillingProvider(): boolean {
    return this.subscription.isAppleBillingProvider;
  }

  get bannerAlertTranslationKey(): string {
    if (this.isPendingApproval) {
      return this.subscription.isAppleBillingProvider
        ? 'COMMON.APPLE_PENDING_APPROVAL_ALERT'
        : 'COMMON.PENDING_APPROVAL_ALERT';
    }

    if (this.isSubscriptionCanceled && this.isExternalBillingProvider) {
      return this.subscription.isAppleBillingProvider
        ? 'ACCOUNT.BILLING.APPLE_CANCELED_MESSAGE'
        : 'ACCOUNT.BILLING.PROVIDER_CANCELED_MESSAGE';
    }

    return this.isSubscriptionPastDue ? 'COMMON.PAYMENT_FAILED_ALERT' : 'COMMON.RESUBSCRIBE_ALERT';
  }

  get bannerActionTranslationKey(): string {
    if (this.isPendingApproval) {
      return 'COMMON.APPROVE_NOW';
    }

    if (this.isSubscriptionCanceled && this.isExternalBillingProvider) {
      return 'COMMON.VIEW_BILLING';
    }

    return this.isSubscriptionPastDue ? this.paymentFailedTranslationKey : 'COMMON.VIEW_PLANS';
  }

  get showNavSidebar(): boolean {
    if (this.hideSidebarForGuidedSetup) {
      return false;
    }

    return !this.user.hasTrialExpired;
  }

  // Guided setup is a focused, sidebar-less experience for every variant; the
  // sidebar returns at the Go Live step. Shared with the mobile burger
  // (nav-sidebar) via GuidedSetupService so both match.
  private get hideSidebarForGuidedSetup(): boolean {
    return this.setup.shouldHideNavForGuidedSetup;
  }

  get showStatusBanner(): boolean {
    return this.setup.isGuidedSetupCompleted && this.user.isUserOnFreeMinutesBillingVersion && !this.agent.primary?.configuration?.liveBannerDismissed;
  }

  get isLiveMode(): boolean {
    return this.subscription?.hasCreditCardOnFile && this.setup.isGuidedSetupCompleted && this.user.isUserOnFreeMinutesBillingVersion;
  }

  get isNewTrialAccount(): boolean {
    if (!this.user.userData?.createdAt) return false;
    const createdAt = new Date(this.user.userData.createdAt);
    const threeMinutesAgo = new Date(Date.now() - 3 * 60 * 1000);
    return createdAt > threeMinutesAgo;
  }

  get showGuidedSetupTrialBanner(): boolean {
    // The immersive setup steps (Train/Test/Customize) show no global chrome;
    // the trial banner first appears on the Go Live step (ENG-2201 QA).
    if (this.setup.isOnPreLaunchSetupStep) {
      return false;
    }
    if (this.user.isUserOnTrialBasedBilling && !this.user.subscription && this.isNewTrialAccount) {
      return true;
    }
    return this.user.isUserOnTrialBasedBilling && !!+this.user.subscription?.trialDaysLeft && !this.isSubscriptionCanceled;
  }

  get trialDaysLeft(): number {
    return this.user.subscription?.trialDaysLeft || (this.isNewTrialAccount ? 7 : 0);
  }

  get isMobileVersion(): boolean {
    return this.session.isMobileVersion;
  }

  handlePaymentFailed() {
    if (this.isPendingApproval) {
      // Apple IAP doesn't use confirmation URLs — purchases happen in-app
      if (this.subscription.isAppleBillingProvider) {
        return;
      }

      const url = this.subscription.providerConfirmationUrl;
      if (url) {
        window.open(url, '_top');
      } else {
        // No cached confirmation URL (fresh pending-approval, or it was already
        // consumed) — don't silently no-op. Send the merchant to the Billing page,
        // whose plan cards mint a fresh approval URL for the right plan.
        this.router.navigate(['/account', { section: 'billing' }]);
      }

      return;
    }

    if (this.isPaymentFailedTriggered) {
      this.isRefreshingState = true;
      this.subscription.refreshPaymentState().subscribe(() => this.isRefreshingState = false);
      return;
    }

    if (this.isSubscriptionPastDue) {
      this.analytics.trackEvent(AnalyticsEvent.PaymentFailedBannerAction);
      this.subscription.redirectToBillingPortal();
    } else if (this.isExternalBillingProvider) {
      // External providers manage resubscription through their own platform
      this.router.navigate(['/account', { section: 'billing' }]);
    } else {
      this.analytics.trackEvent(AnalyticsEvent.ResubscribeBannerAction);
      this.router.navigate(['/account', { section: 'billing' }]);
      if (document.location?.href?.includes('billing')) {
        document.querySelector('rosie-subscription-plans-checkout')?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  completeSetup() {
    this.setup.goToStep(GuidedSetupStepType.Launch);
  }

  unpauseFromBanner() {
    this.router.navigate(['/account', { section: 'billing' }]);
  }

  dismissLiveBanner() {
    this.agent.updateAgentConfiguration({
      configuration: { liveBannerDismissed: true }
    }).subscribe();
  }
}
